<template>
  <Teleport to="body">
    <Transition name="modal-overlay-fade">
      <div
        v-if="visible"
        class="i-modal-overlay"
        :style="{ zIndex: currentZIndex }"
        @click.self="handleMaskClick"
        @wheel="preventBackdropScroll"
        @touchmove="preventBackdropScroll"
      >
        <!-- 动态过渡名称：居中用 zoom，四周用 slide -->
        <Transition :name="transitionName" appear>
          <div
            v-if="visible"
            class="i-modal-wrapper"
            :class="[`is-${position}`]"
            :style="wrapperStyle"
            role="dialog"
            aria-modal="true"
            @wheel.stop
            @touchmove.stop
          >
            <!-- 内容插槽 -->
            <div class="i-modal-content">
              <slot></slot>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
// 模块级 z-index 计数器：必须声明在 <script>（模块作用域）而非 <script setup> 中。
// 若声明在 <script setup> 内，每个组件实例会各持一份独立计数器，
// 导致所有弹框 z-index 都相同（如都是 2001），嵌套弹框（弹框内再开弹框）时
// 层级退化为依赖 DOM 顺序，内层弹框可能被外层遮罩压住而不可见。
let globalZIndex = 2000;
const getZIndex = () => ++globalZIndex;
</script>

<script setup>
import { ref, computed, watch } from 'vue';
import { useZIndex } from 'element-plus';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // 位置：right(默认), top, bottom, left, right
  position: {
    type: String,
    default: 'right',
    validator: (val) => ['center', 'top', 'bottom', 'left', 'right'].includes(val)
  },
  // 尺寸：如果是 center，可以是 '50%' 或 '600px'；如果是抽屉，通常是 '300px' 或 '50%'
  size: { type: String, default: '50%' },
  // 点击遮罩是否关闭
  closeOnClickModal: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue', 'open', 'close']);

const visible = ref(props.modelValue);
const currentZIndex = ref(2000);

// 根据位置决定使用的过渡动画类名前缀
const transitionName = computed(() => {
  return props.position === 'center' ? 'modal-zoom' : 'modal-slide';
});

// 动态计算 wrapper 的样式
const wrapperStyle = computed(() => {
  const style = {
    zIndex: currentZIndex.value
  };

  if (props.position === 'center') {
    // 居中模式：使用 width/height，自动 margin
    style.width = props.size;
    style.maxWidth = '100vw';
    style.maxHeight = '100vh';
    style.height = 'auto';
    style.top = '50%';
    style.left = '50%';
    style.transform = 'translate(-50%, -50%)';
    style.borderRadius = '12px';
  } else if (props.position === 'top' || props.position === 'bottom') {
    // 上下抽屉：宽度 100%，高度由 size 控制
    style.width = '100%';
    style.height = props.size;
    style.left = '0';
    style.right = '0';
    style.borderRadius = props.position === 'top' ? '0 0 12px 12px' : '12px 12px 0 0';

    if (props.position === 'top') {
      style.top = '0';
      style.bottom = 'auto';
    } else {
      style.bottom = '0';
      style.top = 'auto';
    }
  } else if (props.position === 'left' || props.position === 'right') {
    // 左右抽屉：高度 100%，宽度由 size 控制
    style.height = '100%';
    style.width = props.size;
    style.maxWidth = '90vw';
    style.top = '0';
    style.bottom = '0';
    style.borderRadius = props.position === 'left' ? '0 12px 12px 0' : '12px 0 0 12px';

    if (props.position === 'left') {
      style.left = '0';
      style.right = 'auto';
    } else {
      style.right = '0';
      style.left = 'auto';
    }
  }

  return style;
});

// Element Plus 的浮层（抽屉、下拉、日期选择器等）与上面的全局计数器各自独立，
// 且 EP 的计数器会被每次浮层打开推高；长时间使用后 EP 层级可能反超本组件，
// 导致弹框被抽屉/弹层压住。因此打开时取两者较大值，保证始终位于当前所有浮层之上。
const { nextZIndex } = useZIndex();

const updateZIndex = () => {
  currentZIndex.value = Math.max(getZIndex(), nextZIndex());
};

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      updateZIndex();
      visible.value = true;
      emit('open');
      // 禁止背景滚动
      document.body.style.overflow = 'hidden';
    } else {
      visible.value = false;
      emit('close');
      // 恢复背景滚动 (延迟一点等待动画结束)
      setTimeout(() => {
        if (!props.modelValue) document.body.style.overflow = '';
      }, 300);
    }
  },
  { immediate: true }
);

const handleMaskClick = () => {
  if (props.closeOnClickModal) {
    handleCancel();
  }
};

const handleCancel = () => {
  emit('update:modelValue', false);
};

// 仅拦截直接落在遮罩背景上的滚动，避免底层页面滚动，同时不影响弹框内容区的正常滚动。
const preventBackdropScroll = (event) => {
  if (event.target === event.currentTarget) {
    event.preventDefault();
  }
  event.stopPropagation();
};

defineExpose({
  close: handleCancel
});
</script>

<style scoped>
/* --- 遮罩层动画 --- */
.modal-overlay-fade-enter-active,
.modal-overlay-fade-leave-active {
  transition: opacity 0.4s ease;
}
.modal-overlay-fade-enter-from,
.modal-overlay-fade-leave-to {
  opacity: 0;
}

.i-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7); /* 半透明黑底 */
  display: flex;
  backdrop-filter: blur(4px);
  /* 如果是居中，flex 有助于垂直水平居中，但我们要用 absolute 定位，所以这里主要是占位 */
}

/* --- 容器基础样式 --- */
.i-modal-wrapper {
  position: fixed;
  background-color: #131519; /* 你的深色主题背景 */
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: contain;
  color: #c9d1d9;
}

.i-modal-content {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* 弹框本身滚动条样式 */
.i-modal-content::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.i-modal-content::-webkit-scrollbar-track {
  background: transparent;
}
.i-modal-content::-webkit-scrollbar-thumb {
  background: #5b6171;
  border-radius: 10px;
}
.i-modal-content::-webkit-scrollbar-thumb:hover {
  background: #8f949e;
}

/* 弹框子元素滚动条样式 */
.i-modal-content ::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.i-modal-content ::-webkit-scrollbar-track {
  background: transparent;
}
.i-modal-content ::-webkit-scrollbar-thumb {
  background: #5b6171;
  border-radius: 10px;
}
.i-modal-content ::-webkit-scrollbar-thumb:hover {
  background: #8f949e;
}

/* --- 居中动画 (Zoom) --- */
.modal-zoom-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-zoom-leave-active {
  transition: all 0.3s ease-in;
}
.modal-zoom-enter-from,
.modal-zoom-leave-to {
  opacity: 0;
  /* 注意：transform 在 style 中已经定义了 translate(-50%, -50%)，这里只需要处理 scale */
  transform: translate(-50%, -50%) scale(0.9);
}

/* --- 四周滑动动画 (Slide) --- */
.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: transform 0.4s ease-in-out;
}

/* Top */
.is-top.modal-slide-enter-from,
.is-top.modal-slide-leave-to {
  transform: translateY(-100%);
}
/* Bottom */
.is-bottom.modal-slide-enter-from,
.is-bottom.modal-slide-leave-to {
  transform: translateY(100%);
}
/* Left */
.is-left.modal-slide-enter-from,
.is-left.modal-slide-leave-to {
  transform: translateX(-100%);
}
/* Right */
.is-right.modal-slide-enter-from,
.is-right.modal-slide-leave-to {
  transform: translateX(100%);
}

/* 针对非居中模式，需要移除 style 中的 translate(-50%, -50%) 干扰，
   但因为我们在 style 里是根据 position 动态计算的，
   非居中模式 style.transform 是 undefined，所以不会冲突。
   唯独 center 模式 style 里有 transform，所以 modal-zoom 里要兼容。
   
   上面 modal-zoom 的写法已经兼容了：
   enter-from: translate(-50%, -50%) scale(0.9) -> 保持居中但缩小
*/
</style>
